import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"

interface AlertModalState {
  open: boolean
  title: string
  description: string
  variant?: "default" | "success" | "warning" | "destructive"
}

interface ModalContextValue {
  cautionOpen: boolean
  openCaution: () => void
  closeCaution: () => void
  alert: AlertModalState
  showAlert: (
    description: string,
    title?: string,
    variant?: AlertModalState["variant"],
  ) => void
  closeAlert: () => void
}

const ModalContext = createContext<ModalContextValue | null>(null)

const initialAlertState: AlertModalState = {
  open: false,
  title: "",
  description: "",
  variant: "default",
}

/**
 * Global modal orchestration. Replaces every page's hand-copied Caution
 * fraud-warning modal and ad hoc `ShowMsgPopup`/`RegisterStartupScript`
 * alert popups (migration plan §1.3/§2.4) with one shared source of truth,
 * rendered once via <ModalRoot/> in AppLayout.
 */
export function ModalProvider({ children }: { children: ReactNode }) {
  const [cautionOpen, setCautionOpen] = useState(false)
  const [alert, setAlert] = useState<AlertModalState>(initialAlertState)

  const openCaution = useCallback(() => setCautionOpen(true), [])
  const closeCaution = useCallback(() => setCautionOpen(false), [])

  const showAlert = useCallback(
    (
      description: string,
      title = "Notice",
      variant: AlertModalState["variant"] = "default",
    ) => {
      setAlert({ open: true, title, description, variant })
    },
    [],
  )
  const closeAlert = useCallback(() => setAlert((a) => ({ ...a, open: false })), [])

  const value = useMemo(
    () => ({ cautionOpen, openCaution, closeCaution, alert, showAlert, closeAlert }),
    [cautionOpen, openCaution, closeCaution, alert, showAlert, closeAlert],
  )

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
}

export function useGlobalModals() {
  const ctx = useContext(ModalContext)
  if (!ctx) throw new Error("useGlobalModals must be used within ModalProvider")
  return ctx
}
